/**
 * Notes:
 * Image editing is a little more involved then simply updating a field. When updating a field like the
 * company name the result of the edit is the exact same type as what was received from the server (i.e.
 * a string). When editing an image what we are doing is creating local blobs that we can point to via a
 * URL. However this URL is not something we could save to the server because it relies on the existence of
 * the local blob. So when we save we have to take the extra step of saving the image to a remote location and
 * then using this remote URL. This means we need an intermediate step that of saving the image blob references
 * as state.
 */

import { cardEditContext } from '@context/card-edit-context';
import { CardHeaderEditContext } from '@context/card-header-edit-2';
import { cardModeContext } from '@context/card-mode-context';
import { cardPreviewContext } from '@context/card-preview-context';
import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { useCardImageMutations } from '@hooks/card/image';
import { useCardImageBlobs } from '@hooks/card/image/use-card-image-blobs';
import { useCardImageUrls } from '@hooks/card/image/use-card-image-urls';
import type { SettableNode } from '@hooks/use-node';
import { useBlockAttributes } from 'app-next/[workspace]/cards/hooks/use-block-attribibute';
import { useSetCardProperty } from 'app-next/[workspace]/cards/hooks/use-set-card-property';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CardLayout } from 'types';

import { CardFlavours } from '../types';
import { canDeleteCardItem } from '../utils';
import styles from './CardHeader.module.scss';
import { LeftPicture, RightPicture } from './CardHeaderPictures';
import { BannerPicture } from './CardHeaderPictures/BannerPicture';
import { EditCardImageButton } from './EditCardImageButton';
import { EditPicturePopover } from './EditPicturePopover';
import NextImagePopover from './NextImagePopover';
import ProfilePictureDisabledPopover from './ProfilePictureDisabledPopover';
import { SelectImageTypePopover } from './SelectImageTypePopover';
import type { CardAppearance, ImageQuality, ImageType, PlaceholderImageSize } from './types';
import {
  type Locations,
  WEB_EDITOR_VERSION,
  assertIsCardLayout,
  canUseLayout,
  defaultTypeFromPopover,
  getSuggestedLayout,
  getSuggestedLayoutFromCardImages,
  imageLocationsFromLayout,
  shouldUseSuggestedLayout,
  typeToFieldId,
} from './utilities';
import type { CardAttributeType, CardImage } from '.cache/__types__';

export * from './utilities';

export type CardHeaderProps = {
  card: Maybe<CardDetailsFragment>;
  node?: SettableNode<HTMLHeadingElement> | undefined;
  createMode: boolean;
  appearance?: CardAppearance;
  layout?: CardLayout;
  isTemplate: boolean;
  picturePreview?: ImageType | undefined;
  placeholderImageSize?: PlaceholderImageSize;
  onBannerImageLoad?: () => void;
  onFloatingLeftImageLoad?: () => void;
  onFloatingRightImageLoad?: () => void;
};

/**  re-usable interface for blob information
 */

const CardHeader: React.FC<React.PropsWithChildren<CardHeaderProps>> = ({
  card,
  createMode,
  isTemplate,
  appearance = 'standalone',
  layout,
  node,
  placeholderImageSize = 'normal',
  onBannerImageLoad,
  onFloatingLeftImageLoad,
  onFloatingRightImageLoad,
}) => {
  const layoutProperty = card?.properties?.layout;

  assertIsCardLayout(layoutProperty?.value);

  const {
    showEditPopover,
    setShowEditPopover,
    showSelectPopover,
    setShowSelectPopover,
    editState,
    setEditImage,
    previewImageLayout,
  } = useContext(CardHeaderEditContext);
  const { flavour } = useContext(cardEditContext);
  const { hideEmptyAttributes } = useContext(cardPreviewContext);
  const { mode } = useContext(cardModeContext);

  const layoutFromCard = layout ?? layoutProperty?.value ?? previewImageLayout;

  const [askForNextImage, setAskForNextImage] = useState<boolean | undefined>();
  const [imageLayout, setImageLayout] = useState<CardLayout>(layoutFromCard ?? CardLayout.Layout_1B);
  const [showCantAddProfilePopover, setShowCantAddProfilePopover] = useState<boolean | undefined>();

  const cardId = card?.id;
  const templateId = card?.template?.id;
  const userId = card?.profile?.id;
  const workspaceId = card?.workspace_id;
  const interactive = true;

  const { updateOrCreate, update, remove } = useCardImageMutations();
  const { mutate: setProperty } = useSetCardProperty();

  const { isAttributeBlocked } = useBlockAttributes({
    categoryAllowList: ['image'],
    card,
  });

  const cardImages = card?.images;

  const canLock = true;

  // Keep the layout from the card up to date with the layout in state.
  useEffect(() => {
    if (layoutFromCard) {
      setImageLayout(layoutFromCard);
    }
  }, [layoutFromCard]);

  // compute the layout we should have, based on incoming layout and which image has been clicked
  useEffect(() => {
    if (layoutFromCard) {
      return;
    }
    if (showEditPopover?.position === 'left') {
      return setImageLayout(CardLayout.Layout_2C);
    }
    if (showEditPopover?.position === 'banner') {
      return setImageLayout(CardLayout.Layout_1B);
    }
    return setImageLayout(CardLayout.Layout_1B);
  }, [layoutFromCard, showEditPopover?.position]);

  const imgBlobs = useCardImageBlobs(cardImages);
  const imgUrls = useCardImageUrls({
    editMode: Boolean(mode === 'edit'),
    layout: imageLayout,
    cardImages,
    flavour,
  });

  const imageLocations = useMemo(() => {
    const locations = imageLocationsFromLayout(imageLayout);
    return locations;
  }, [imageLayout]);

  // this is for hiding the placeholder images
  // on some pages we want to display placeholder images and on other pages we want to hide them, this is governed by hideEmptyAttributes
  useEffect(() => {
    // early return so this useEffect isn't executed if hideEmptyAttributes not specified as true
    if (!hideEmptyAttributes) {
      return;
    }

    const layoutIsValid = canUseLayout(
      {
        cover: imgUrls.cover?.edited_url,
        logo: imgUrls.logo?.edited_url,
        profile: imgUrls.profile?.edited_url,
      },
      imageLayout,
    );
    if (layoutIsValid) {
      return;
    }

    const noPlaceholdersLayout = getSuggestedLayout({
      cover: imgUrls.cover?.edited_url,
      logo: imgUrls.logo?.edited_url,
      profile: imgUrls.profile?.edited_url,
    });

    setImageLayout(noPlaceholdersLayout ?? CardLayout.Layout_1C);
  }, [imageLayout, imgUrls, hideEmptyAttributes]);

  const handleEditPictureClose = useCallback(() => {
    imgBlobs.profile.resetImageUrl();
    imgBlobs.cover.resetImageUrl();
    imgBlobs.logo.resetImageUrl();
    setShowEditPopover(undefined);
    setEditImage({ image: undefined, type: undefined });
  }, [imgBlobs.cover, imgBlobs.logo, imgBlobs.profile, setEditImage, setShowEditPopover]);

  const saveLayout = useCallback(
    (newLayout?: CardLayout) => {
      if (!cardId) {
        return;
      }

      newLayout = newLayout ?? imageLayout;
      if (newLayout) {
        setImageLayout(newLayout);
      }

      if (layoutProperty?.value === String(newLayout)) {
        return;
      }
      setProperty({
        cardId,
        propertyId: layoutProperty?.id,
        attributeName: 'layout',
        value: newLayout,
      });
    },
    [cardId, layoutProperty?.value, imageLayout, setProperty, layoutProperty?.id],
  );

  const onRemovePicture = useCallback(async () => {
    if (!cardId) {
      return;
    }

    const type = defaultTypeFromPopover(showEditPopover, imageLayout);

    imgBlobs[type].updateImageUrl(undefined);

    const image = imgUrls?.[type];
    if (!image || !image.id) {
      return;
    }

    const canDelete =
      flavour === CardFlavours.INVITE_TOKEN_CLAIM
        ? false
        : canDeleteCardItem({
            flavour,
            isOptional: image.is_optional,
            isTemplated: Boolean(image?.template_id),
            valueProvider: image.value_provider,
          });

    if (!canDelete) {
      if (!editState.image?.id) {
        return;
      }

      update(
        {
          is_locked: editState.image.is_locked,
          is_optional: editState.image.is_optional,
          is_profile_linked: editState.image.is_profile_linked,
          stage: 'todo',
          template_id: editState.image.template_id,
          value_provider: editState.image.value_provider,
          id: editState.image.id,
          card_id: card?.id,
          edited_url: null,
          original_url: null,
          metadata: null,
        },
        editState.image.attribute_type as CardAttributeType,
      );
      return;
    }

    await remove(image.id, cardId, image.attribute_type?.name);

    // Set the image which was just removed to undefined (only if fully removing the image) here so that the new layout is calculated correctly
    // without the removed image.
    // imgUrls doesn't get updated to have the image removed until the new card comes thru from the cache - which is async
    const newLayout = getSuggestedLayoutFromCardImages({
      cover: type === 'cover' ? undefined : imgUrls?.cover,
      profile: type === 'profile' ? undefined : imgUrls?.profile,
      logo: type === 'logo' ? undefined : imgUrls?.logo,
      type: undefined,
    });

    if (newLayout) {
      setImageLayout(newLayout);
      saveLayout(newLayout);
    }
  }, [
    cardId,
    showEditPopover,
    imageLayout,
    imgBlobs,
    imgUrls,
    flavour,
    remove,
    editState,
    update,
    card?.id,
    saveLayout,
  ]);

  const onSelectImage = useCallback(
    (image: Partial<CardImage>) => {
      const type = defaultTypeFromPopover(showEditPopover, imageLayout);
      const newUrl = image.edited_url || image.original_url;
      if (showEditPopover) {
        setShowEditPopover({ type, ...showEditPopover });
      }

      if (!newUrl) {
        return;
      }

      // update the layout here automatically, so they get the best view of all their pictures.
      imgBlobs[type].updateImageUrl(newUrl);

      // If templated layout is locked, don't override it
      const templatedLayout = card?.properties?.layout?.template;
      if (templatedLayout?.value_locked) {
        setEditImage({ image, type });
        return;
      }

      const suggestedLayout = getSuggestedLayoutFromCardImages({
        cover: imgUrls?.cover,
        profile: imgUrls?.profile,
        logo: imgUrls?.logo,
        type,
      });

      if (!suggestedLayout) {
        setEditImage({ image, type });
        return;
      }

      if (shouldUseSuggestedLayout(imageLayout, suggestedLayout)) {
        setImageLayout(suggestedLayout);
      }

      setEditImage({ image, type });
    },
    [
      showEditPopover,
      imageLayout,
      imgBlobs,
      card?.properties?.layout?.template,
      setEditImage,
      setShowEditPopover,
      imgUrls,
    ],
  );

  const handleImageUpdate = useCallback(
    (image: Partial<CardImage>, quality?: ImageQuality) => {
      if (showEditPopover) {
        const type = defaultTypeFromPopover(showEditPopover, imageLayout);

        setEditImage({ image: { ...image, metadata: { ...image.metadata } }, type, quality });
      }
    },
    [imageLayout, setEditImage, showEditPopover],
  );

  // this effect shows a popover when a use uploads a photo that's not a profile
  const handleCheckAskForNextImage = useCallback(() => {
    if (createMode && askForNextImage === undefined) {
      // potentially open the next image if there's something in the banner
      const imageCount = [
        imgUrls.profile?.original_url,
        imgUrls.logo?.original_url,
        imgUrls.cover?.original_url,
      ].filter(Boolean).length;
      if (
        imageCount === 1 && // only ask when 1 image has been added
        !templateId && // don't ask when presets applied
        !isTemplate && // dont ask for preset
        (imageLocations.banner === 'logo' || imageLocations.banner === 'cover') && // don't ask unless the other is there
        !imgUrls.profile?.original_url && // don't ask if profile exist
        (imgUrls.logo?.original_url || imgUrls.cover?.original_url) && // only ask if another image has been added
        !imgUrls?.profile?.is_locked // don't ask if profile is locked
      ) {
        setAskForNextImage(true);
      } else if (imageCount > 1) {
        setAskForNextImage(false);
      }
    }
  }, [
    createMode,
    askForNextImage,
    imgUrls.profile?.original_url,
    imgUrls.profile?.is_locked,
    imgUrls.logo?.original_url,
    imgUrls.cover?.original_url,
    templateId,
    isTemplate,
    imageLocations.banner,
  ]);

  const onPictureSave = useCallback(
    async (image: Partial<CardImage>) => {
      const type = defaultTypeFromPopover(showEditPopover, imageLayout);
      imgBlobs[type].resetImageUrl();
      if (!cardId) {
        return;
      }
      const matchingItem = card.images?.[typeToFieldId(type)];

      const isUploadingFirstImage = !imgBlobs[type].imageUrl;
      // Only calculate the new layout if the image is being added for the first time.
      const newLayout = isUploadingFirstImage
        ? getSuggestedLayoutFromCardImages({
            cover: imgUrls?.cover,
            profile: imgUrls?.profile,
            logo: imgUrls?.logo,
            type,
          })
        : undefined;

      if (newLayout) {
        setImageLayout(newLayout);
      }

      await Promise.allSettled([
        saveLayout(newLayout),
        updateOrCreate(
          {
            ...image,
            id: matchingItem ? matchingItem.id : undefined,
            card_id: cardId,
            stage: null,
            is_locked: false,
            attribute_type: undefined,
            metadata: {
              ...image.metadata,
              editor_version: image.metadata?.editor_version ?? null,
              background_color: image.metadata?.background_color ?? null,
            },
          },
          typeToFieldId(type),
          image.attribute_type as CardAttributeType,
        ),
      ]);
      handleCheckAskForNextImage();
    },
    [
      showEditPopover,
      imageLayout,
      imgBlobs,
      cardId,
      card?.images,
      imgUrls?.cover,
      imgUrls?.profile,
      imgUrls?.logo,
      saveLayout,
      updateOrCreate,
      handleCheckAskForNextImage,
    ],
  );

  const isBannerBlocked = isAttributeBlocked(imgUrls.banner?.attribute_type?.id);

  // for the next layout, calculate whether we will preview an image
  const nextLayout = getSuggestedLayout({
    cover: imgUrls.cover?.edited_url || imgUrls.cover?.id || editState?.type === 'cover',
    logo: imgUrls.logo?.edited_url || editState?.type === 'logo',
    profile: imgUrls.profile?.edited_url || imgUrls?.profile.id || editState?.type === 'profile', // don't preview profile on preset
  });

  const nextLocations = imageLocationsFromLayout(nextLayout ?? CardLayout.Layout_1B);

  const willPreview = useCallback(
    (location: keyof Locations) => {
      const next =
        nextLocations[location] && editState?.type === nextLocations[location] ? editState?.type : undefined;
      return next;
    },
    [editState?.type, nextLocations],
  );

  const shouldPreviewProfile = useMemo(() => {
    const hasAlreadyUploadedProfile = Boolean(cardImages?.profile_picture?.id);
    if (hasAlreadyUploadedProfile) {
      return false;
    }
    return willPreview('left');
  }, [cardImages?.profile_picture?.id, willPreview]);

  const shouldPreviewLogo = useMemo(() => {
    const hasAlreadyUploadedLogo = Boolean(cardImages?.company_logo?.id);
    if (hasAlreadyUploadedLogo) {
      return false;
    }
    return willPreview('right');
  }, [cardImages?.company_logo?.id, willPreview]);

  const defaultImage = useMemo(() => {
    const type = defaultTypeFromPopover(showEditPopover, imageLayout);
    const imageRef =
      showEditPopover?.position && imgUrls[showEditPopover.position]
        ? imgUrls[showEditPopover.position]
        : imgUrls[type];
    return imageRef;
  }, [showEditPopover, imgUrls, imageLayout]);

  const handleBannerEditClicked = useCallback(() => {
    if (imgUrls?.banner?.is_locked) {
      return false;
    }

    if (imgUrls.banner?.edited_url || imgUrls.banner?.value_provider) {
      setShowEditPopover({ type: imageLocations.banner });
    } else {
      setShowSelectPopover(true);
    }
  }, [imageLocations.banner, imgUrls, setShowEditPopover, setShowSelectPopover]);

  const getQuality = useCallback(
    (type?: ImageType) => {
      if (editState?.type === type && showEditPopover?.type === type) {
        return editState?.quality;
      }
    },
    [editState?.type, editState?.quality, showEditPopover?.type],
  );

  return (
    <header
      className={styles['card-header']}
      data-card-layout={imageLayout}
      // if cover photo is empty, and you want to hide empty photos, don't apply the style associated with this data attribute
      data-image-type={
        imageLocations.banner && !imgUrls[imageLocations.banner]?.edited_url && hideEmptyAttributes
          ? ''
          : imageLocations.banner
      }
      ref={node ? node[1] : undefined}
      data-appearance={appearance}
      data-edit-mode={Boolean(mode === 'edit')}
      data-empty={!imgUrls.banner?.edited_url && !imgUrls.banner?.value_provider}
      data-floating-left={imageLocations.left ?? shouldPreviewProfile}
      data-has-floating-images={
        !!(imageLocations.left || imageLocations.right) || !!(willPreview('right') || willPreview('left'))
      }
    >
      {/* this is the overlay that shows when you hover over the banner */}
      {mode === 'edit' && !isBannerBlocked && !imgUrls.banner?.is_locked && (
        <div className={styles['edit-banner-image-overlay']}>
          <EditCardImageButton location="banner" onClick={handleBannerEditClicked} />
        </div>
      )}

      <BannerPicture
        canLock={canLock}
        editorVersion={imgUrls?.banner?.metadata?.editor_version ?? WEB_EDITOR_VERSION}
        img={imgUrls?.banner}
        appearance={appearance}
        quality={getQuality(imageLocations.banner)}
        type={imageLocations.banner || 'cover'}
        placeholderImageSize={placeholderImageSize}
        onImageLoad={onBannerImageLoad}
      />

      {(imageLocations.left || shouldPreviewProfile) && (
        <LeftPicture
          alt="Profile"
          canLock={canLock}
          disable={imgBlobs.profile.error}
          onClick={() => setShowEditPopover({ position: 'left', ...showEditPopover })}
          quality={getQuality(imageLocations.left)}
          type={imageLocations.left ?? willPreview('left') ?? 'profile'}
          url={imgUrls.left?.edited_url}
          image={imgUrls.left}
          interactive={interactive}
          placeholderImageSize={placeholderImageSize}
          onImageLoad={onFloatingLeftImageLoad}
        />
      )}
      {(imageLocations.right || shouldPreviewLogo) && (
        <RightPicture
          canLock={canLock}
          type={imageLocations.right || 'logo'}
          url={imgUrls.right?.edited_url}
          alt="Logo"
          onClick={() => setShowEditPopover({ position: 'right', ...showEditPopover })}
          disable={imgBlobs.logo.error}
          quality={getQuality(imageLocations.right)}
          interactive={interactive}
          image={imgUrls.right}
          placeholderImageSize={placeholderImageSize}
          onImageLoad={onFloatingRightImageLoad}
        />
      )}

      {mode === 'edit' && (
        <>
          <EditPicturePopover
            isTemplate={isTemplate}
            cardId={cardId}
            templateId={templateId}
            workspaceId={workspaceId}
            userId={userId}
            targetNode={node ? node[0] : undefined}
            show={!!showEditPopover}
            onClose={handleEditPictureClose}
            onUpdate={handleImageUpdate}
            imageReference={editState?.image ? editState?.image : defaultImage}
            onRemove={onRemovePicture}
            onSave={onPictureSave}
            onSelect={onSelectImage}
            type={defaultTypeFromPopover(showEditPopover, imageLayout)}
            canLock={canLock}
          />

          <SelectImageTypePopover
            imgUrls={imgUrls}
            onClose={() => {
              setShowSelectPopover(false);
            }}
            onSelect={(type) => {
              setShowSelectPopover(false);
              setShowEditPopover({ position: 'banner', type });
            }}
            show={showSelectPopover}
            targetNode={node ? node[0] : undefined}
          />
          <NextImagePopover
            onYes={(type) => {
              setShowEditPopover({ type, position: 'left' });
              setAskForNextImage(false);
            }}
            show={askForNextImage}
            onClose={() => setAskForNextImage(false)}
            targetNode={node ? node[0] : undefined}
          />
          <ProfilePictureDisabledPopover
            show={showCantAddProfilePopover}
            onClose={() => setShowCantAddProfilePopover(false)}
            targetNode={node ? node[0] : undefined}
          />
        </>
      )}
    </header>
  );
};

export default CardHeader;
